import React, { useState } from 'react'
import styled from '@emotion/styled'
import { ResolvedDay } from '../types'
import {
  DAY_STATUS_COLORS,
  DAY_STATUS_BORDER_COLORS,
  TIME_SOURCE_COLORS,
  CUTOFF_EMOJI,
  DROPOFF_EMOJI,
} from '../constants'
import CalendarMonth from './CalendarMonth'
import { toISODate } from '../utils'

interface Props {
  year: number
  resolvedDays: Map<string, ResolvedDay>
  holidays: Map<string, string>
  onYearChange: (year: number) => void
  onDayClick: (date: string) => void
}

const YearlyCalendarView: React.FC<Props> = ({
  year,
  resolvedDays,
  holidays,
  onYearChange,
  onDayClick,
}) => {
  const todayStr = toISODate(new Date())
  const [showHolidays, setShowHolidays] = useState(false)

  return (
    <Container>
      <YearNav>
        <NavButton onClick={() => onYearChange(year - 1)}>
          ← {year - 1}
        </NavButton>
        <YearLabel>{year}</YearLabel>
        <NavButton onClick={() => onYearChange(year + 1)}>
          {year + 1} →
        </NavButton>
      </YearNav>

      <Legend>
        <LegendItem>
          <LegendSwatch
            color={DAY_STATUS_COLORS.service}
            border={DAY_STATUS_BORDER_COLORS.service}
          />
          Service Day
        </LegendItem>
        <LegendItem>
          <LegendSwatch
            color={DAY_STATUS_COLORS['ad-hoc-on']}
            border={DAY_STATUS_BORDER_COLORS['ad-hoc-on']}
          />
          Ad Hoc Day On
        </LegendItem>
        <LegendItem>
          <LegendSwatch
            color={DAY_STATUS_COLORS.cancelled}
            border={DAY_STATUS_BORDER_COLORS.cancelled}
          />
          Cancelled
        </LegendItem>
        <LegendItem>
          <LegendSwatch
            color={DAY_STATUS_COLORS['no-schedule']}
            border="#E0E0E0"
          />
          No Service
        </LegendItem>
        <LegendDivider />
        <LegendItem>{CUTOFF_EMOJI} Cutoff</LegendItem>
        <LegendItem>{DROPOFF_EMOJI} Dropoff</LegendItem>
        <LegendDivider />
        <LegendItem>
          <CutoffSwatch color={TIME_SOURCE_COLORS.global} />
          Global Default
        </LegendItem>
        <LegendItem>
          <CutoffSwatch color={TIME_SOURCE_COLORS.schedule} />
          Schedule Default
        </LegendItem>
        <LegendItem>
          <CutoffSwatch color={TIME_SOURCE_COLORS.rule} />
          Rule Override
        </LegendItem>
        <LegendItem>
          <CutoffSwatch color={TIME_SOURCE_COLORS.override} />
          Date Override
        </LegendItem>
        <LegendDivider />
        <LegendItem>
          <HolidayDotSwatch />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={showHolidays}
              onChange={(e) => setShowHolidays(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Show National Holidays
          </label>
        </LegendItem>
        <LegendDivider />
        <HoverHint>
          Hover a date for full details · Click to add an override
        </HoverHint>
      </Legend>

      <MonthsGrid>
        {Array.from({ length: 12 }, (_, i) => (
          <CalendarMonth
            key={i}
            year={year}
            month={i}
            resolvedDays={resolvedDays}
            holidays={showHolidays ? holidays : undefined}
            todayStr={todayStr}
            onDayClick={onDayClick}
          />
        ))}
      </MonthsGrid>
    </Container>
  )
}

export default YearlyCalendarView

// ── Styled Components ──

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const YearNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 12px;
`

const NavButton = styled.button`
  background: none;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #424242;

  &:hover {
    background: #f5f5f5;
  }
`

const YearLabel = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #212121;
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #616161;
`

const LegendSwatch = styled.div<{ color: string; border: string }>`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: ${({ color }) => color};
  border: 1.5px solid ${({ border }) => border};
`

const CutoffSwatch = styled.div<{ color: string }>`
  width: 14px;
  height: 3px;
  border-radius: 1px;
  background: ${({ color }) => color};
`

const LegendDivider = styled.div`
  width: 1px;
  height: 16px;
  background: #e0e0e0;
`

const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`

const HoverHint = styled.span`
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
`

const HolidayDotSwatch = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff8f00;
  flex-shrink: 0;
`
